import { DestroyRef } from '@angular/core';
import type { GridOrchestrationDataAccess } from './grid-orchestration-data-access';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EMPTY, Observable, catchError, expand, map, of, take, tap, toArray } from 'rxjs';
import { GridRowsPage, GridGroupSummaryItem, GridGroupSummaryPage } from '../contracts/public-api';
import type { GridBackendAttributeFilter, ColumnFilterMap } from '../d-data-grid/column-filter.types';
import type { DataGridColumn } from '../d-data-grid/data-grid.types';
import { mergeAttributeFilters as mergeAttributeFilterEntries } from './grid-orchestration-attribute-filter';
import {
  GROUP_PATH_DELIMITER,
  NULL_BACKEND_SENTINEL,
  NULL_DISPLAY_KEY
} from './grid-orchestration.constants';
import type { GroupPaginationState } from './grid-orchestration.types';
import {
  BUCKET_EAGER_THRESHOLD,
  GROUP_HEADER_PAGE_SIZE,
  normalizeGroupSummaryKey
} from './grid-orchestration.utils';

/**
 * State accessors needed by group-summary fetching and header pagination functions.
 * Implemented by {@link GridOrchestrationService} via {@link buildGroupSummaryHost}.
 */
export interface GroupSummaryHost {
  readonly elementsProvider: GridOrchestrationDataAccess;
  readonly destroyRef: DestroyRef;
  getColumnFilters: () => ColumnFilterMap;
  getSharedColumns: () => Array<DataGridColumn>;
  getTypeSpecificColumns: () => Record<string, Array<DataGridColumn>>;
  getRemappedFields: () => ReadonlySet<string>;
  getLastGridWhere: () => Record<string, unknown>;
  getGroupPagination: () => Map<string, GroupPaginationState> | null;
  setGroupPagination: (pagination: Map<string, GroupPaginationState> | null) => void;
  updateGroupPagination: (
    updater: (current: Map<string, GroupPaginationState>) => Map<string, GroupPaginationState>
  ) => void;
  getPrimaryGroupField: () => string | null;
  getDiscriminatorField: () => string;
  getGridGroupByFields: () => Array<string>;
  getGroupSummaryHasNextPage: () => boolean;
  setGroupSummaryHasNextPage: (value: boolean) => void;
  getGroupSummaryEndCursor: () => string | null;
  setGroupSummaryEndCursor: (cursor: string | null) => void;
  getGroupHeaderPagesLoaded: () => number;
  setGroupHeaderPagesLoaded: (value: number) => void;
  incrementGroupHeaderPagesLoaded: () => void;
  getAllPrimaryGroupSummaries: () => Array<GridGroupSummaryItem>;
  setAllPrimaryGroupSummaries: (groups: Array<GridGroupSummaryItem>) => void;
  getPrimarySummaryTotalDistinctCount: () => number;
  setPrimarySummaryTotalDistinctCount: (count: number) => void;
  getQueryGeneration: () => number;
  fetchPerGroupFirstPagesForSkeleton: (
    field: string,
    groups: Array<GridGroupSummaryItem>,
    baseWhere: Record<string, unknown>
  ) => Observable<GridRowsPage>;
  mergeGroupPaginationIntoElements: () => void;
}

// ── Pure helpers ─────────────────────────────────────────────────────────────

/** Counts top-level primary buckets in group pagination (excludes nested path keys). */
export function countPrimaryPaginationBuckets(pagination: Map<string, GroupPaginationState>): number {
  return Array.from(pagination.keys()).filter((key: string): boolean => !key.includes(GROUP_PATH_DELIMITER))
    .length;
}

/** Returns true when another server-side primary group summary page can be fetched. */
export function canFetchMorePrimaryGroupSummaries(host: GroupSummaryHost): boolean {
  return (
    host.getGroupSummaryHasNextPage() &&
    host.getPrimaryGroupField() !== null &&
    host.getGroupSummaryEndCursor() !== null
  );
}

/** Returns true when seeded pagination has more primary headers than currently displayed. */
export function hasUnrevealedPrimaryGroupHeaders(host: GroupSummaryHost): boolean {
  const pagination: Map<string, GroupPaginationState> | null = host.getGroupPagination();
  const primaryField: string | null = host.getPrimaryGroupField();
  const groupFields: Array<string> = host.getGridGroupByFields();
  if (
    pagination === null ||
    primaryField === null ||
    groupFields.length === 0 ||
    primaryField === host.getDiscriminatorField()
  ) {
    return false;
  }
  const primaryBucketCount: number = countPrimaryPaginationBuckets(pagination);
  const headerPageLimit: number = GROUP_HEADER_PAGE_SIZE * host.getGroupHeaderPagesLoaded();
  return primaryBucketCount > headerPageLimit;
}

// ── Skeleton seeding ─────────────────────────────────────────────────────────

/**
 * Seeds `_groupPagination` from a high-cardinality primary group's summary
 * without fetching any rows. Each entry carries the server count and is marked
 * `hasNextPage: true` so the grid can lazily load the bucket's first page when
 * it's expanded.
 *
 * @returns An empty response whose `totalCount` is the sum across buckets.
 */
export function seedPrimaryGroupSkeleton(
  host: GroupSummaryHost,
  groups: Array<GridGroupSummaryItem>
): GridRowsPage {
  const paginationMap: Map<string, GroupPaginationState> = new Map<string, GroupPaginationState>();
  let totalCount: number = 0;
  for (const group of groups) {
    const key: string = normalizeGroupSummaryKey(group.value, NULL_BACKEND_SENTINEL, NULL_DISPLAY_KEY);
    paginationMap.set(key, {
      nodes: [],
      endCursor: null,
      hasNextPage: true,
      totalCount: group.count,
      loading: false,
      pagesLoaded: 0
    });
    totalCount += group.count;
  }
  host.setGroupPagination(paginationMap);
  return { nodes: [], totalCount };
}

/**
 * Seeds high-cardinality primary headers and eagerly loads first pages for
 * buckets whose record count is at or below {@link BUCKET_EAGER_THRESHOLD}.
 */
export function seedPrimaryGroupSkeletonWithHybridEager(
  host: GroupSummaryHost,
  field: string,
  groups: Array<GridGroupSummaryItem>,
  baseWhere: Record<string, unknown>
): Observable<GridRowsPage> {
  const skeleton: GridRowsPage = seedPrimaryGroupSkeleton(host, groups);
  const eagerGroups: Array<GridGroupSummaryItem> = groups.filter(
    (group: GridGroupSummaryItem): boolean => group.count <= BUCKET_EAGER_THRESHOLD
  );
  if (eagerGroups.length === 0) {
    return of(skeleton);
  }
  return host.fetchPerGroupFirstPagesForSkeleton(field, eagerGroups, baseWhere).pipe(
    map((eagerResponse: GridRowsPage): GridRowsPage => {
      const pagination: Map<string, GroupPaginationState> | null = host.getGroupPagination();
      if (pagination !== null) {
        host.mergeGroupPaginationIntoElements();
      }
      return {
        nodes: eagerResponse.nodes,
        totalCount: skeleton.totalCount
      };
    })
  );
}

// ── Pagination API fetching ───────────────────────────────────────────────────

/**
 * Fetches all pages of the primary group summary by paginating until exhausted.
 * Falls back to the non-paginated `getGroupSummary` API on error.
 *
 * @param field - Primary group field.
 * @param elementTypes - Selected element types.
 * @param where - Base filter.
 */
export function fetchAllPrimaryGroupSummaries(
  host: GroupSummaryHost,
  field: string,
  elementTypes: Array<string>,
  where: Record<string, unknown>
): Observable<Array<GridGroupSummaryItem>> {
  const attributeFilter: Array<GridBackendAttributeFilter> | undefined = mergeAttributeFilterEntries(
    host.getColumnFilters(),
    host.getSharedColumns(),
    host.getTypeSpecificColumns(),
    host.getRemappedFields()
  );

  const loadPage: (after: string | null | undefined) => Observable<GridGroupSummaryPage> = (
    after: string | null | undefined
  ): Observable<GridGroupSummaryPage> =>
    host.elementsProvider
      .getGroupSummaryPaginated(field, elementTypes, {
        where,
        first: GROUP_HEADER_PAGE_SIZE,
        ...(attributeFilter !== undefined ? { attributeFilter } : {}),
        ...(after != null ? { after } : {})
      })
      .pipe(take(1));

  return loadPage(undefined).pipe(
    expand((page: GridGroupSummaryPage): Observable<GridGroupSummaryPage> =>
      page.pageInfo.hasNextPage && page.pageInfo.endCursor != null ? loadPage(page.pageInfo.endCursor) : EMPTY
    ),
    toArray(),
    map((pages: Array<GridGroupSummaryPage>): Array<GridGroupSummaryItem> => {
      const groups: Array<GridGroupSummaryItem> = pages.flatMap(
        (page: GridGroupSummaryPage): Array<GridGroupSummaryItem> => [...page.nodes]
      );
      const lastPage: GridGroupSummaryPage = pages[pages.length - 1];
      host.setAllPrimaryGroupSummaries(groups);
      host.setGroupSummaryEndCursor(lastPage.pageInfo.endCursor);
      host.setPrimarySummaryTotalDistinctCount(lastPage.totalDistinctCount);
      const allDistinctSummariesLoaded: boolean =
        lastPage.totalDistinctCount > 0 && groups.length >= lastPage.totalDistinctCount;
      host.setGroupSummaryHasNextPage(lastPage.pageInfo.hasNextPage && !allDistinctSummariesLoaded);
      host.setGroupHeaderPagesLoaded(1);
      return groups;
    }),
    catchError((): Observable<Array<GridGroupSummaryItem>> => {
      return host.elementsProvider.getGroupSummary(field, elementTypes, where, attributeFilter).pipe(
        take(1),
        tap((groups: Array<GridGroupSummaryItem>): void => {
          host.setAllPrimaryGroupSummaries([...groups]);
          host.setGroupSummaryEndCursor(null);
          host.setGroupSummaryHasNextPage(false);
          host.setPrimarySummaryTotalDistinctCount(groups.length);
          host.setGroupHeaderPagesLoaded(1);
        })
      );
    })
  );
}

/**
 * Fetches a single page of the primary group summary (used by header pagination).
 *
 * @param field - Primary group field.
 * @param elementTypes - Selected element types.
 * @param where - Base filter.
 * @param after - Cursor from previous page.
 */
export function fetchPrimaryGroupSummaryPage(
  host: GroupSummaryHost,
  field: string,
  elementTypes: Array<string>,
  where: Record<string, unknown>,
  after: string | null | undefined
): Observable<GridGroupSummaryPage> {
  const attributeFilter: Array<GridBackendAttributeFilter> | undefined = mergeAttributeFilterEntries(
    host.getColumnFilters(),
    host.getSharedColumns(),
    host.getTypeSpecificColumns(),
    host.getRemappedFields()
  );
  return host.elementsProvider
    .getGroupSummaryPaginated(field, elementTypes, {
      where,
      first: GROUP_HEADER_PAGE_SIZE,
      ...(attributeFilter !== undefined ? { attributeFilter } : {}),
      ...(after != null ? { after } : {})
    })
    .pipe(take(1));
}

// ── Header pagination ─────────────────────────────────────────────────────────

/**
 * Loads the next page of primary group headers (paginated summary or client slice).
 * Called by the public `loadMoreGroupHeaders` method on the service.
 */
export function loadMoreGroupHeaders(host: GroupSummaryHost): void {
  if (hasUnrevealedPrimaryGroupHeaders(host)) {
    host.incrementGroupHeaderPagesLoaded();
    return;
  }

  if (!canFetchMorePrimaryGroupSummaries(host)) {
    return;
  }

  const primaryField: string | null = host.getPrimaryGroupField();
  const lastGridWhere: Record<string, unknown> = host.getLastGridWhere();
  const discriminatorField: string = host.getDiscriminatorField();
  const elementTypes: Array<string> =
    discriminatorField.length === 0
      ? []
      : (((lastGridWhere[discriminatorField] as { in?: Array<string> })?.in ?? []) as Array<string>);
  const queryGeneration: number = host.getQueryGeneration();

  fetchPrimaryGroupSummaryPage(
    host,
    primaryField!,
    elementTypes,
    lastGridWhere,
    host.getGroupSummaryEndCursor()!
  )
    .pipe(
      take(1),
      takeUntilDestroyed(host.destroyRef),
      catchError((): Observable<GridGroupSummaryPage> => {
        if (host.getQueryGeneration() !== queryGeneration) {
          return EMPTY;
        }
        host.incrementGroupHeaderPagesLoaded();
        return EMPTY;
      }),
      tap((page: GridGroupSummaryPage): void => {
        if (host.getQueryGeneration() !== queryGeneration) {
          return;
        }
        host.setAllPrimaryGroupSummaries([...host.getAllPrimaryGroupSummaries(), ...page.nodes]);
        host.setGroupSummaryEndCursor(page.pageInfo.endCursor);
        host.setPrimarySummaryTotalDistinctCount(page.totalDistinctCount);
        const allDistinctSummariesLoaded: boolean =
          page.totalDistinctCount > 0 && host.getAllPrimaryGroupSummaries().length >= page.totalDistinctCount;
        host.setGroupSummaryHasNextPage(page.pageInfo.hasNextPage && !allDistinctSummariesLoaded);
        host.incrementGroupHeaderPagesLoaded();

        host.updateGroupPagination(
          (pagination: Map<string, GroupPaginationState>): Map<string, GroupPaginationState> => {
            const next: Map<string, GroupPaginationState> = new Map<string, GroupPaginationState>(pagination);
            for (const group of page.nodes) {
              const key: string = normalizeGroupSummaryKey(
                group.value,
                NULL_BACKEND_SENTINEL,
                NULL_DISPLAY_KEY
              );
              if (!next.has(key)) {
                next.set(key, {
                  nodes: [],
                  endCursor: null,
                  hasNextPage: true,
                  totalCount: group.count,
                  loading: false,
                  pagesLoaded: 0
                });
              }
            }
            return next;
          }
        );
      })
    )
    .subscribe();
}
